import { useState } from 'react';
import { PriceAdjustment } from '@la/types';
import { formatAsUSD } from '@la/utilities';
import {
  BillingSummaryItemLineItem,
  CombinedCartItemData,
} from '../Checkout.types';
import PaymentPlansModal, {
  getDiscountDisplayText,
} from '../PaymentPlansModal/PaymentPlansModal';
import styles from './OrderSummaryCard.module.scss';
import OrderSummaryCardBody from './OrderSummaryCardBody';
import OrderSummaryCardHeader from './OrderSummaryCardHeader';

export type CartSummaryCardProps = {
  cartItemData: CombinedCartItemData;
  isShowingAdditionalButtons?: boolean;
};

/* OrderSummaryCard */
export default function OrderSummaryCard({
  cartItemData,
  isShowingAdditionalButtons,
}: Readonly<CartSummaryCardProps>) {
  const {
    addOns,
    cartItemName,
    cartItemUuid,
    editUrl,
    iconAltText,
    iconUrl,
    lineItems,
    location,
    paymentPlanSummaries,
    price,
    programDateInfo,
    selectedPaymentPlan,
    subprograms,
    subtype,
    totalAmount,
    type,
  } = cartItemData;

  const [isPaymentPlansModalOpen, setIsPaymentPlansModalOpen] = useState(false);

  const adjustedPrice = totalAmount
    ? formatAsUSD(parseFloat(totalAmount))
    : null;

  const discountCodeBadgeLabel = lineItems
    ? getDiscountCodeBadgeLabel(lineItems)
    : null;

  const paymentPlanBadgeLabel = selectedPaymentPlan?.priceAdjustment
    ? getPaymentPlanBadgeLabel(selectedPaymentPlan.priceAdjustment)
    : null;

  return (
    <>
      <div className={styles.orderSummaryCard}>
        <OrderSummaryCardHeader
          adjustedPrice={adjustedPrice}
          cartItemName={cartItemName}
          iconAltText={iconAltText}
          iconUrl={iconUrl}
          originalPrice={price}
          subtype={subtype}
          type={type}
        />
        <OrderSummaryCardBody
          addOns={addOns}
          cartItemUuid={cartItemUuid}
          discountCodeBadgeLabel={discountCodeBadgeLabel}
          editUrl={editUrl}
          isShowingAdditionalButtons={isShowingAdditionalButtons}
          location={location}
          openPaymentPlansModal={() => setIsPaymentPlansModalOpen(true)}
          paymentPlanBadgeLabel={paymentPlanBadgeLabel}
          programDateInfo={programDateInfo}
          selectedPaymentPlan={selectedPaymentPlan}
          subprograms={subprograms}
          subtype={subtype ?? ''}
        />
      </div>
      {isPaymentPlansModalOpen ? (
        <PaymentPlansModal
          cartItemName={cartItemName}
          cartItemUuid={cartItemUuid}
          closeModal={() => setIsPaymentPlansModalOpen(false)}
          open={isPaymentPlansModalOpen}
          paymentPlanPrice={price}
          paymentPlanSummaries={paymentPlanSummaries ?? []}
          selectedPaymentPlan={selectedPaymentPlan}
        />
      ) : null}
    </>
  );
}
/* */

function getDiscountCodeBadgeLabel(lineItems: BillingSummaryItemLineItem[]) {
  const discountCode = lineItems?.[0]?.discountCodes?.[0] ?? null;
  let priceAdjustment;
  if (discountCode?.fixedAmount) {
    priceAdjustment = formatAsUSD(
      Math.abs(parseFloat(discountCode.fixedAmount))
    );
  } else if (discountCode?.percentage) {
    priceAdjustment = `${Math.abs(parseFloat(discountCode.percentage))}%`;
  }

  return discountCode && priceAdjustment
    ? `"${discountCode.code}" ${priceAdjustment}`
    : null;
}

function getPaymentPlanBadgeLabel(priceAdjustment: PriceAdjustment) {
  return getDiscountDisplayText(priceAdjustment);
}
